import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Data } from "@puckeditor/core";
import type { StyleState } from "@ease/page-builder";
import { STYLE_STATES } from "@ease/page-builder";

import { getLegacyAnimationProps } from "./dss/animation-normalizers";
import type { Breakpoint, ResponsiveConfig } from "./dss/responsive-overrides";
import {
  collectBlockSummaries,
  getBlockById,
  reorderLayerData,
  updateBlockById,
  type BlockSummary,
} from "./layer-data";
import type { BlockStyleData } from "./style-state-system";
import { blockStyleToStyleMap, extractBlockStyleStates } from "./style-state-system";

export interface StylePanelSection {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  group:
    | "background"
    | "typography"
    | "border"
    | "transform"
    | "spacing"
    | "position"
    | "responsive"
    | "animation"
    | "custom";
}

export interface StylePanelContextValue {
  data: Data;
  blocks: BlockSummary[];
  selectedComponentId: string | null;
  selectedComponentType: string | null;
  setSelectedComponentId: (id: string | null) => void;
  activeState: StyleState;
  setActiveState: (state: StyleState) => void;
  editorBreakpoint: Breakpoint;
  hiddenComponentIds: ReadonlySet<string>;
  lockedComponentIds: ReadonlySet<string>;
  toggleComponentHidden: (id: string) => void;
  toggleComponentLocked: (id: string) => void;
  reorderComponents: (dragId: string, dropId: string, position: "before" | "after" | "inside") => void;
  getSelectedBlockStyles: () => Partial<Record<StyleState, Partial<BlockStyleData>>>;
  getSelectedStyleState: () => Partial<BlockStyleData> | undefined;
  setSelectedStyleState: (style: Partial<BlockStyleData> | undefined) => void;
  getSelectedComponentProps: () => Record<string, unknown> | undefined;
  setSelectedComponentProps: (props: Record<string, unknown> | undefined) => void;
  replaceSelectedDssStyles: (styleData: Record<string, unknown> | undefined) => void;
  copiedDssStyles: Record<string, unknown> | null;
  copySelectedDssStyles: () => void;
  pasteCopiedDssStyles: () => void;
  getSelectedResponsiveConfig: () => ResponsiveConfig | undefined;
  setSelectedResponsiveConfig: (config: ResponsiveConfig | undefined) => void;
}

const StylePanelContext = createContext<StylePanelContextValue | null>(null);

export function useStylePanel(): StylePanelContextValue {
  const ctx = useContext(StylePanelContext);
  if (!ctx) {
    throw new Error("useStylePanel must be used within <StylePanelProvider>");
  }
  return ctx;
}

interface StylePanelProviderProps {
  data: Data;
  onDataChange: (data: Data) => void;
  editorBreakpoint: Breakpoint;
  onSelectionChange?: (selection: { id: string | null; type: string | null }) => void;
  children: React.ReactNode;
}

type EditorDssStyles = BlockStyleData & {
  states?: Partial<Record<StyleState, Partial<BlockStyleData>>>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function cloneStyleData<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function toClipboardStyleData(styleData: Record<string, unknown>): Record<string, unknown> {
  const {
    elementId: _elementId,
    classNames: _classNames,
    customAttributes: _customAttributes,
    ...visualStyles
  } = styleData;

  return cloneStyleData(visualStyles);
}

function isEmptyStyleState(style: Partial<BlockStyleData> | undefined): boolean {
  if (!style) return true;
  return Object.values(style).every((value) => value === undefined || value === null || value === "");
}

function extractDssStates(props: Record<string, unknown>): Partial<Record<StyleState, Partial<BlockStyleData>>> {
  if (isRecord(props._dssStyles)) {
    const dssStyles = props._dssStyles as EditorDssStyles;
    const states: Partial<Record<StyleState, Partial<BlockStyleData>>> = {};
    const { states: nestedStates, ...normal } = dssStyles;
    if (Object.keys(normal).length > 0) {
      states.normal = normal;
    }
    if (nestedStates?.hover) states.hover = nestedStates.hover;
    if (nestedStates?.focus) states.focus = nestedStates.focus;
    if (nestedStates?.active) states.active = nestedStates.active;
    return states;
  }

  const legacyStates = extractBlockStyleStates(props);
  const states: Partial<Record<StyleState, Partial<BlockStyleData>>> = {};
  if (legacyStates.normal) states.normal = legacyStates.normal as Partial<BlockStyleData>;
  if (legacyStates.hover) states.hover = legacyStates.hover as Partial<BlockStyleData>;
  if (legacyStates.focus) states.focus = legacyStates.focus as Partial<BlockStyleData>;
  if (legacyStates.active) states.active = legacyStates.active as Partial<BlockStyleData>;
  return states;
}

function setSelectedBlockStyles(
  data: Data,
  componentId: string,
  state: StyleState,
  style: Partial<BlockStyleData> | undefined,
): Data {
  return updateBlockById(data, componentId, (block) => {
    const props = isRecord(block.props) ? block.props : {};
    const currentStates = extractDssStates(props);
    const nextStates: Partial<Record<StyleState, Partial<BlockStyleData>>> = { ...currentStates };

    if (isEmptyStyleState(style)) {
      delete nextStates[state];
    } else {
      nextStates[state] = style;
    }

    const nextProps: Record<string, unknown> = { ...props };
    const statePayload = nextStates.hover || nextStates.focus || nextStates.active
      ? {
          states: {
            ...(nextStates.hover ? { hover: nextStates.hover } : {}),
            ...(nextStates.focus ? { focus: nextStates.focus } : {}),
            ...(nextStates.active ? { active: nextStates.active } : {}),
          },
        }
      : {};

    const nextDssStyles: EditorDssStyles = {
      ...(nextStates.normal ?? {}),
      ...statePayload,
    };

    if (STYLE_STATES.some((key) => nextStates[key] !== undefined)) {
      nextProps._dssStyles = nextDssStyles;
      delete nextProps._styles;
    } else {
      delete nextProps._dssStyles;
      delete nextProps._styles;
    }

    const legacyAnimationProps = getLegacyAnimationProps(nextStates.normal?.animation);
    if (legacyAnimationProps.animationType) {
      nextProps.animationType = legacyAnimationProps.animationType;
      nextProps.animationDelay = legacyAnimationProps.animationDelay;
      nextProps.animationDuration = legacyAnimationProps.animationDuration;
    } else {
      delete nextProps.animationType;
      delete nextProps.animationDelay;
      delete nextProps.animationDuration;
    }

    return {
      ...block,
      props: nextProps,
    };
  });
}

function createEmptyResponsiveConfig(): ResponsiveConfig {
  return {
    desktop: {},
    tablet: {},
    mobile: {},
  };
}

function setSelectedResponsiveStyleState(
  data: Data,
  componentId: string,
  breakpoint: Breakpoint,
  style: Partial<BlockStyleData> | undefined,
): Data {
  return updateBlockById(data, componentId, (block) => {
    const props = isRecord(block.props) ? block.props : {};
    const currentStates = extractDssStates(props);
    const currentNormal = currentStates.normal;
    const currentResponsive = currentNormal?.responsive ?? createEmptyResponsiveConfig();
    const nextResponsive = {
      ...currentResponsive,
      [breakpoint]: {
        ...currentResponsive[breakpoint],
        ...blockStyleToStyleMap(style),
      },
    };

    const nextProps: Record<string, unknown> = { ...props };
    const statePayload = currentStates.hover || currentStates.focus || currentStates.active
      ? {
          states: {
            ...(currentStates.hover ? { hover: currentStates.hover } : {}),
            ...(currentStates.focus ? { focus: currentStates.focus } : {}),
            ...(currentStates.active ? { active: currentStates.active } : {}),
          },
        }
      : {};

    nextProps._dssStyles = {
      ...(currentNormal ?? {}),
      responsive: nextResponsive,
      ...statePayload,
    };
    delete nextProps._styles;

    return {
      ...block,
      props: nextProps,
    };
  });
}

export function StylePanelProvider({
  data,
  onDataChange,
  editorBreakpoint,
  onSelectionChange,
  children,
}: StylePanelProviderProps) {
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const [activeState, setActiveState] = useState<StyleState>("normal");
  const [hiddenComponentIds, setHiddenComponentIds] = useState<ReadonlySet<string>>(new Set());
  const [lockedComponentIds, setLockedComponentIds] = useState<ReadonlySet<string>>(new Set());
  const [copiedDssStyles, setCopiedDssStyles] = useState<Record<string, unknown> | null>(null);

  const blocks = useMemo(() => collectBlockSummaries(data), [data]);

  useEffect(() => {
    if (blocks.length === 0) {
      setSelectedComponentId(null);
      return;
    }

    if (!selectedComponentId || !blocks.some((block) => block.id === selectedComponentId)) {
      setSelectedComponentId(blocks[0]!.id);
    }
  }, [blocks, selectedComponentId]);

  const selectedBlock = useMemo(
    () => blocks.find((block) => block.id === selectedComponentId) ?? null,
    [blocks, selectedComponentId],
  );

  useEffect(() => {
    onSelectionChange?.({
      id: selectedComponentId,
      type: selectedBlock?.type ?? null,
    });
  }, [onSelectionChange, selectedBlock?.type, selectedComponentId]);

  const getSelectedStyleState = useCallback((): Partial<BlockStyleData> | undefined => {
    if (!selectedComponentId) return undefined;
    const item = getBlockById(data, selectedComponentId);
    if (!item) return undefined;
    const props = isRecord(item.props) ? item.props : {};
    const styles = extractDssStates(props);
    return styles[activeState];
  }, [activeState, data, selectedComponentId]);

  const getSelectedBlockStyles = useCallback((): Partial<Record<StyleState, Partial<BlockStyleData>>> => {
    if (!selectedComponentId) return {};
    const item = getBlockById(data, selectedComponentId);
    if (!item) return {};
    const props = isRecord(item.props) ? item.props : {};
    return extractDssStates(props);
  }, [data, selectedComponentId]);

  const setSelectedStyleState = useCallback(
    (style: Partial<BlockStyleData> | undefined) => {
      if (!selectedComponentId) return;
      if (activeState === "normal" && editorBreakpoint !== "desktop") {
        onDataChange(
          setSelectedResponsiveStyleState(data, selectedComponentId, editorBreakpoint, style),
        );
        return;
      }
      onDataChange(setSelectedBlockStyles(data, selectedComponentId, activeState, style));
    },
    [activeState, data, editorBreakpoint, onDataChange, selectedComponentId],
  );

  const getSelectedComponentProps = useCallback((): Record<string, unknown> | undefined => {
    if (!selectedComponentId) return undefined;
    const item = getBlockById(data, selectedComponentId);
    if (!item || !isRecord(item.props)) return undefined;
    return item.props;
  }, [data, selectedComponentId]);

  const setSelectedComponentProps = useCallback(
    (props: Record<string, unknown> | undefined) => {
      if (!selectedComponentId || !props) return;
      onDataChange(updateBlockById(data, selectedComponentId, (block) => ({
        ...block,
        props,
      })));
    },
    [data, onDataChange, selectedComponentId],
  );

  const replaceSelectedDssStyles = useCallback(
    (styleData: Record<string, unknown> | undefined) => {
      if (!selectedComponentId) return;

      onDataChange(updateBlockById(data, selectedComponentId, (block) => {
        const props = isRecord(block.props) ? { ...block.props } : {};

        if (styleData && Object.keys(styleData).length > 0) {
          props._dssStyles = { ...styleData };
          delete props._styles;

          const animation = isRecord(styleData.animation)
            ? styleData.animation
            : undefined;
          const legacyAnimationProps = getLegacyAnimationProps(animation);

          if (legacyAnimationProps.animationType) {
            props.animationType = legacyAnimationProps.animationType;
            props.animationDelay = legacyAnimationProps.animationDelay;
            props.animationDuration = legacyAnimationProps.animationDuration;
          } else {
            delete props.animationType;
            delete props.animationDelay;
            delete props.animationDuration;
          }

          if (legacyAnimationProps.transition) {
            props.transition = legacyAnimationProps.transition;
          } else {
            delete props.transition;
          }

          if (legacyAnimationProps.transform) {
            props.transform = legacyAnimationProps.transform;
          } else {
            delete props.transform;
          }
        } else {
          delete props._dssStyles;
          delete props._styles;
          delete props.animationType;
          delete props.animationDelay;
          delete props.animationDuration;
          delete props.transition;
          delete props.transform;
        }

        return {
          ...block,
          props,
        };
      }));
    },
    [data, onDataChange, selectedComponentId],
  );

  const copySelectedDssStyles = useCallback(() => {
    const props = getSelectedComponentProps();
    if (!isRecord(props?._dssStyles)) return;
    setCopiedDssStyles(toClipboardStyleData(props._dssStyles));
  }, [getSelectedComponentProps]);

  const pasteCopiedDssStyles = useCallback(() => {
    if (!copiedDssStyles) return;
    replaceSelectedDssStyles(cloneStyleData(copiedDssStyles));
  }, [copiedDssStyles, replaceSelectedDssStyles]);

  const getSelectedResponsiveConfig = useCallback((): ResponsiveConfig | undefined => {
    return getSelectedBlockStyles().normal?.responsive;
  }, [getSelectedBlockStyles]);

  const setSelectedResponsiveConfig = useCallback(
    (config: ResponsiveConfig | undefined) => {
      if (!selectedComponentId) return;
      const currentNormalState = getSelectedBlockStyles().normal;
      const nextNormalStyle: Partial<BlockStyleData> | undefined = config
        ? { ...(currentNormalState ?? {}), responsive: config }
        : (() => {
            if (!currentNormalState) return undefined;
            const { responsive: _responsive, ...rest } = currentNormalState;
            return rest;
          })();
      onDataChange(setSelectedBlockStyles(data, selectedComponentId, "normal", nextNormalStyle));
    },
    [data, getSelectedBlockStyles, onDataChange, selectedComponentId],
  );

  const toggleComponentHidden = useCallback((id: string) => {
    setHiddenComponentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleComponentLocked = useCallback((id: string) => {
    setLockedComponentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const reorderComponents = useCallback(
    (dragId: string, dropId: string, position: "before" | "after" | "inside") => {
      onDataChange(reorderLayerData(data, dragId, dropId, position));
    },
    [data, onDataChange],
  );

  const value = useMemo<StylePanelContextValue>(
    () => ({
      data,
      blocks,
      selectedComponentId,
      selectedComponentType: selectedBlock?.type ?? null,
      setSelectedComponentId,
      activeState,
      setActiveState,
      editorBreakpoint,
      hiddenComponentIds,
      lockedComponentIds,
      toggleComponentHidden,
      toggleComponentLocked,
      reorderComponents,
      getSelectedBlockStyles,
      getSelectedStyleState,
      setSelectedStyleState,
      getSelectedComponentProps,
      setSelectedComponentProps,
      replaceSelectedDssStyles,
      copiedDssStyles,
      copySelectedDssStyles,
      pasteCopiedDssStyles,
      getSelectedResponsiveConfig,
      setSelectedResponsiveConfig,
    }),
    [
      activeState,
      blocks,
      data,
      editorBreakpoint,
      getSelectedBlockStyles,
      getSelectedComponentProps,
      getSelectedResponsiveConfig,
      getSelectedStyleState,
      hiddenComponentIds,
      lockedComponentIds,
      reorderComponents,
      replaceSelectedDssStyles,
      copiedDssStyles,
      copySelectedDssStyles,
      pasteCopiedDssStyles,
      selectedBlock?.type,
      selectedComponentId,
      setSelectedComponentProps,
      toggleComponentHidden,
      toggleComponentLocked,
      setSelectedResponsiveConfig,
      setSelectedStyleState,
    ],
  );

  return (
    <StylePanelContext.Provider value={value}>
      {children}
    </StylePanelContext.Provider>
  );
}
